/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  InteractionManager,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useAccountSetup } from '../../shell/providers/AccountSetupProvider';
import { consumePendingEmailConfirmToastIfNeeded } from '../application/emailConfirmToast';
import { createBranchForMyOrganization } from '../../onboarding/application/onboardingService';
import { setAwaitingEmployeeInvitation } from '../application/awaitingInvitation';
import { getErrorMessage } from '../../shared/errorMessage';
import { showError, showSuccessAfterModalClose } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { isBillingEnabled } from '../../shell/billing/isBillingEnabled';
import { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { spacing } from '../../shared/theme/spacing';
import { CONTENT_MAX_WIDTH_COMPACT } from '../../shared/layout/breakpoints'
import { ModalScrollScreen } from '../../shared/layout/ModalScrollScreen'
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { ticketChrome } from '../../shared/ticket/ticketChrome.styles'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader';
import { AuthScreenFrame } from '../../shell/brand/AuthScreenFrame';
import { AppLogoMark } from '../../shell/brand/AppLogoMark';
import { OrganizationSetupWizard } from './onboarding/OrganizationSetupWizard';
import { AUTH_ACCENT_INK, authFormUi } from './components/authFormUi';
import { settingsStyles } from '../../settings/screens/settingsScreen.styles';

type Props = {
  /** Tras crear org/sucursal y actualizar stores; típicamente refresh de entitlements. */
  onComplete?: () => void | Promise<void>;
  /**
   * Modal "Agregar sucursal" desde el stack (`CreateBranch`).
   * El shell de App monta esta pantalla fuera de un Screen — no usar useRoute acá.
   */
  asCreateBranch?: boolean;
  /** Wizard para alta de un segundo negocio (multi-org dueño). */
  asAdditionalOrganization?: boolean;
  /** Antes de crear la org: el shell (App) mantiene onboarding montado para «Listo». */
  onCreateStarted?: () => void;
};

type AddBranchPresentation = 'overlay' | 'onboarding';

type AddBranchSetupFormProps = Props & {
  presentation: AddBranchPresentation;
};

function AddBranchSetupForm({ onComplete, presentation }: AddBranchSetupFormProps) {
  const navigation = useNavigation<any>();
  const { layoutWebShell } = useContentLayout('form');
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);
  const branchNameInputRef = useRef<TextInput>(null);
  const { entitlements } = usePlanEntitlements();
  const [branchName, setBranchName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const dismiss = () => {
    if (!submitting && navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  const handleSubmit = async () => {
    const s = branchName.trim();
    if (!s) {
      setError('Ingresá el nombre de la sucursal.');
      showError('No se pudo continuar', 'Ingresá el nombre de la sucursal.');
      return;
    }

    const maxBranches = entitlements?.max_branches;
    const branchCount = entitlements?.branch_count ?? 0;
    if (isBillingEnabled() && maxBranches != null && branchCount >= maxBranches) {
      const msg = `Tu plan permite hasta ${maxBranches} sucursal${maxBranches === 1 ? '' : 'es'} y ya tenés ${branchCount}. Actualizá tu plan para agregar otra.`;
      setError(msg);
      showError('Límite de plan alcanzado', msg);
      return;
    }

    setError(undefined);
    setSubmitting(true);
    try {
      await createBranchForMyOrganization(s);
      if (navigation.canGoBack()) {
        navigation.goBack();
      }
      showSuccessAfterModalClose('Sucursal agregada', `Ya podés usar ${s}.`);
      InteractionManager.runAfterInteractions(() => {
        void onComplete?.();
      });
    } catch (e: unknown) {
      setError('No se pudo agregar la sucursal. Probá de nuevo.');
      presentUnexpectedFailure({ module: 'auth', op: 'onboarding.create_branch', error: e, userMessage: 'No se pudo agregar la sucursal.' });
    } finally {
      setSubmitting(false);
    }
  };

  const formFields = (
    <>
      <Text style={overlayStyles.lead}>
        Poné un nombre que reconozcas en el mostrador.
      </Text>
      <ModalFormField label="Nombre" required>
        <TextInput
          ref={branchNameInputRef}
          value={branchName}
          onChangeText={setBranchName}
          placeholder="Ej. Sucursal Norte"
          placeholderTextColor={ticketColors.ink3}
          style={settingsStyles.input}
          editable={!submitting}
          returnKeyType="done"
          onSubmitEditing={() => {
            if (!submitting) void handleSubmit();
          }}
          accessibilityLabel="Nombre de la sucursal"
        />
      </ModalFormField>
      {error ? (
        <View style={authFormUi.errorBanner}>
          <Text style={authFormUi.errorBannerText}>{error}</Text>
        </View>
      ) : null}
    </>
  );

  const formFooter = (
    <View style={overlayStyles.foot}>
      <TouchableOpacity
        style={[overlayStyles.softBtn, submitting && overlayStyles.softBtnDisabled]}
        onPress={() => void handleSubmit()}
        disabled={submitting}
        accessibilityRole="button"
        accessibilityLabel="Crear sucursal"
      >
        {submitting ? (
          <ActivityIndicator color="#115E59" />
        ) : (
          <Text style={overlayStyles.softBtnText}>Crear sucursal</Text>
        )}
      </TouchableOpacity>
    </View>
  );

  if (presentation === 'onboarding') {
    return (
      <AuthScreenFrame>
        <ModalScrollScreen
          ref={scrollRef}
          contentRef={scrollContentRef}
          variant="form"
          transparentBackground
          contentContainerStyle={styles.scroll}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={authFormUi.card}>
            <View style={authFormUi.brandBlock}>
              <AppLogoMark size={56} showWordmark />
            </View>
            <View style={styles.head}>
              <Text style={authFormUi.title}>Agregar sucursal</Text>
              <Text style={authFormUi.subtitle}>
                Completá el nombre de la nueva sucursal para comenzar a usarla.
              </Text>
            </View>

            <View style={authFormUi.fieldGroup}>
              <Text style={authFormUi.label}>Nombre de la sucursal</Text>
              <TextInput
                ref={branchNameInputRef}
                value={branchName}
                onChangeText={setBranchName}
                placeholder="Ej. Sucursal principal"
                placeholderTextColor={ticketColors.ink3}
                style={authFormUi.input}
                editable={!submitting}
                returnKeyType="done"
                onSubmitEditing={() => {
                  if (!submitting) void handleSubmit();
                }}
                accessibilityLabel="Nombre de la sucursal"
              />
            </View>

            {error ? (
              <View style={authFormUi.errorBanner}>
                <Text style={authFormUi.errorBannerText}>{error}</Text>
              </View>
            ) : null}

            <TouchableOpacity
              style={[authFormUi.primaryBtn, submitting && authFormUi.primaryBtnDisabled]}
              onPress={() => void handleSubmit()}
              disabled={submitting}
              accessibilityRole="button"
              accessibilityLabel="Agregar sucursal"
            >
              {submitting ? (
                <ActivityIndicator color={AUTH_ACCENT_INK} />
              ) : (
                <Text style={authFormUi.primaryLabel}>Agregar sucursal</Text>
              )}
            </TouchableOpacity>
          </View>
        </ModalScrollScreen>
      </AuthScreenFrame>
    );
  }

  const isDesktopDialog = layoutWebShell;
  const dialogMaxWidth = Math.min(CONTENT_MAX_WIDTH_COMPACT, windowWidth - 48);
  const sheetMaxHeight = windowHeight * 0.88;

  const formBody = (
    <View style={overlayStyles.formBody}>{formFields}</View>
  );

  if (isDesktopDialog) {
    return (
      <View style={overlayStyles.overlayRoot}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={dismiss}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View
          style={[
            ticketChrome.dialogCard,
            overlayStyles.dialogCard,
            { maxWidth: dialogMaxWidth, maxHeight: sheetMaxHeight },
          ]}
        >
          <View style={overlayStyles.dialogHeader}>
            <ModalScreenHeader onClose={dismiss} title="Agregar sucursal" />
          </View>
          {formBody}
          {formFooter}
        </View>
      </View>
    );
  }

  return (
    <View style={overlayStyles.sheetOverlayRoot}>
      <Pressable
        style={StyleSheet.absoluteFill}
        onPress={dismiss}
        accessibilityRole="button"
        accessibilityLabel="Cerrar"
      />
      <View
        style={[
          ticketChrome.sheetCard,
          overlayStyles.sheetCard,
          {
            maxHeight: sheetMaxHeight,
            paddingBottom: Math.max(insets.bottom, spacing.sm),
          },
        ]}
      >
        <View style={ticketChrome.grabber} />
        <View style={overlayStyles.sheetHeader}>
          <ModalScreenHeader onClose={dismiss} title="Agregar sucursal" />
        </View>
        {formBody}
        {formFooter}
      </View>
    </View>
  );
}

export const BranchSetupScreen = ({
  onComplete,
  asCreateBranch = false,
  asAdditionalOrganization = false,
  onCreateStarted,
}: Props) => {
  const isCreateBranchFlow = asCreateBranch;
  const { entitlements, refresh: refreshEntitlements } = usePlanEntitlements();
  const { refreshAccountSetup } = useAccountSetup();
  const hasOrganization = Boolean(entitlements?.organization_id);
  const branchCount = entitlements?.branch_count ?? 0;
  /**
   * Defensa local: tras ensure_my_organization el padre (App) también debe
   * mantener BranchSetup montado; esto cubre si el hold llega un tick tarde.
   */
  const [holdWizardForSuccess, setHoldWizardForSuccess] = useState(false);

  useEffect(() => {
    ensureTicketFonts();
    consumePendingEmailConfirmToastIfNeeded();
  }, []);

  const handleComplete = useCallback(async () => {
    setHoldWizardForSuccess(false);
    await Promise.all([
      refreshEntitlements({ force: true }),
      refreshAccountSetup({ force: true, background: true }),
    ]);
    await onComplete?.();
  }, [refreshEntitlements, refreshAccountSetup, onComplete]);

  const handleCreateStarted = useCallback(() => {
    setHoldWizardForSuccess(true);
    onCreateStarted?.();
  }, [onCreateStarted]);

  /** El usuario no es dueño: guarda la intención y el gate lo manda a esperar el invite. */
  const handleAwaitingInvite = useCallback(async () => {
    const result = await setAwaitingEmployeeInvitation(true);
    if (!result.ok) {
      showError('No pudimos guardarlo', result.message ?? 'Probá de nuevo en un momento.');
      return;
    }
    await refreshAccountSetup({ force: true, background: true });
  }, [refreshAccountSetup]);

  if (isCreateBranchFlow) {
    return <AddBranchSetupForm onComplete={handleComplete} presentation="overlay" />;
  }

  if (asAdditionalOrganization) {
    return <OrganizationSetupWizard onComplete={handleComplete} setupMode="additional" />;
  }

  if (!hasOrganization || holdWizardForSuccess) {
    return (
      <OrganizationSetupWizard
        onComplete={handleComplete}
        onAwaitingInvite={handleAwaitingInvite}
        onCreateStarted={handleCreateStarted}
      />
    );
  }

  if (branchCount > 0) {
    return (
      <View style={styles.blocked}>
        <ActivityIndicator color={ticketColors.gold} />
      </View>
    );
  }

  return <AddBranchSetupForm onComplete={handleComplete} presentation="onboarding" />;
};

const styles = StyleSheet.create({
  scroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: spacing.xl,
  },
  head: {
    gap: 8,
  },
  blocked: {
    flex: 1,
    backgroundColor: ticketColors.bg,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const overlayStyles = StyleSheet.create({
  overlayRoot: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  sheetOverlayRoot: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  dialogCard: {
    width: '100%',
    overflow: 'hidden',
    zIndex: 1,
  },
  sheetCard: {
    width: '100%',
    zIndex: 1,
  },
  dialogHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  sheetHeader: {
    paddingHorizontal: 16,
    paddingBottom: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  formBody: {
    padding: 20,
    gap: 16,
    backgroundColor: ticketColors.paper,
  },
  lead: {
    fontSize: 14,
    lineHeight: 21,
    color: ticketColors.ink2,
  },
  foot: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  softBtn: {
    minHeight: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  softBtnDisabled: {
    opacity: 0.6,
  },
  softBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.brandInk,
  },
});

export const OnboardingScreen = BranchSetupScreen;
